// Core API - Request/Response Interceptors
// Location: src/core/api/interceptors.ts

import {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { handleApiError } from "./errorHandler";
import {
  useAuthStore,
  getAccessToken,
  setAccessToken,
} from "../store/authStore";
import { useProviderStore } from "../store/providerStore";
import { useUIStore } from "../store/uiStore";
import { markRateLimited } from "./rateLimit";
import { authService } from "../../features/auth/services/authService";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: Date;
  };
  _retry?: boolean;
}

export const setupInterceptors = (axiosInstance: AxiosInstance): void => {
  // Request interceptor - Attach stored JWT to every request
  axiosInstance.interceptors.request.use(
    (config: CustomAxiosRequestConfig) => {
      const token = getAccessToken();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const orgId = useProviderStore.getState().activeOrgId;
      if (orgId && !config.headers["X-Org-Id"]) {
        config.headers["X-Org-Id"] = orgId;
      }
      config.metadata = { startTime: new Date() };
      return config;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  // Response interceptor - Handle responses and errors
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (import.meta.env.DEV) {
        const config = response.config as CustomAxiosRequestConfig;
        if (config.metadata?.startTime) {
          const duration =
            new Date().getTime() - config.metadata.startTime.getTime();
          console.log(
            `✅ ${config.method?.toUpperCase()} ${config.url} - ${duration}ms`,
          );
        }
      }

      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as CustomAxiosRequestConfig;

      // Handle 401 - Unauthorized (token expired): try refresh token via cookie
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        try {
          const { accessToken: newToken } = await authService.refreshToken();
          if (newToken) {
            setAccessToken(newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          }
        } catch (err: unknown) {
          console.error(err);
          useAuthStore.getState().logout();
          //window.location.href = '/login?reason=session_updated';
        }
      }

      const handledError = handleApiError(error);

      if (handledError.status === 429) {
        const retryAfter = Number(error.response?.headers?.["retry-after"]);
        markRateLimited(
          Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60,
        );
      } else if (handledError.status !== 400) {
        const { showToast } = useUIStore.getState();
        showToast(handledError.message, "error");
      }

      return Promise.reject(handledError);
    },
  );
};
